import React, { useState, useEffect } from 'react';
import api from '../services/api';
import InputNombre from '../components/InputNombre';
import './Fournisseurs.css';

interface Fournisseur {
  id: number;
  nom: string;
  contact: string;
  telephone: string;
  email: string;
  adresse: string;
  ville: string;
  pays: string;
  ifu: string;
  rccm: string;
  categorie: string;
  totalAchats: number;
  nombreCommandes: number;
  delaiLivraison: string;
  conditionsPaiement: string;
  actif: boolean;
  notes: string;
  createdAt: string;
}

interface Stats {
  total: number;
  actifs: number;
  totalAchats: number;
}

const fournisseurVide = {
  nom: '',
  contact: '',
  telephone: '',
  email: '',
  adresse: '',
  ville: 'Ouagadougou',
  pays: 'Burkina Faso',
  ifu: '',
  rccm: '',
  categorie: '',
  delaiLivraison: '',
  conditionsPaiement: '',
  notes: '',
};

const categories = [
  'Informatique & Réseau',
  'Téléphonie',
  'Électronique',
  'Fournitures bureau',
  'Électroménager',
  'Import Chine',
  'Import Dubai',
  'Local Burkina',
  'Autre',
];

const fmt = (n: number) => Number(n).toLocaleString('fr-FR');

export default function Fournisseurs() {
  const [fournisseurs, setFournisseurs] = useState<Fournisseur[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [recherche, setRecherche] = useState('');
  const [chargement, setChargement] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fournisseurSel, setFournisseurSel] = useState<Fournisseur | null>(null);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [form, setForm] = useState(fournisseurVide);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');

  useEffect(() => {
    chargerFournisseurs();
    chargerStats();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => chargerFournisseurs(), 300);
    return () => clearTimeout(timer);
  }, [recherche]);

  const chargerFournisseurs = async () => {
    try {
      const res = await api.get('/fournisseurs', {
        params: recherche ? { recherche } : {}
      });
      setFournisseurs(res.data);
    } catch (e) {
      setErreur('Impossible de charger les fournisseurs');
    } finally {
      setChargement(false);
    }
  };

  const chargerStats = async () => {
    try {
      const res = await api.get('/fournisseurs/stats');
      setStats(res.data);
    } catch (e) {}
  };

  const ouvrirCreation = () => {
    setEnEdition(null);
    setForm(fournisseurVide);
    setErreur('');
    setShowModal(true);
  };

  const ouvrirEdition = (f: Fournisseur) => {
    setEnEdition(f.id);
    setForm({ ...fournisseurVide, ...f });
    setErreur('');
    setShowModal(true);
    setShowDetail(false);
  };

  const ouvrirDetail = (f: Fournisseur) => {
    setFournisseurSel(f);
    setShowDetail(true);
  };

  const sauvegarder = async () => {
    if (!form.nom) {
      setErreur('Le nom est obligatoire');
      return;
    }
    try {
      if (enEdition) {
        await api.put(`/fournisseurs/${enEdition}`, form);
        setSucces('Fournisseur modifié avec succès');
      } else {
        await api.post('/fournisseurs', form);
        setSucces('Fournisseur créé avec succès');
      }
      setShowModal(false);
      chargerFournisseurs();
      chargerStats();
      setTimeout(() => setSucces(''), 3000);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Une erreur est survenue');
    }
  };

  const supprimer = async () => {
    if (!fournisseurSel) return;
    try {
      await api.delete(`/fournisseurs/${fournisseurSel.id}`);
      setSucces('Fournisseur supprimé');
      setShowDeleteModal(false);
      setShowDetail(false);
      chargerFournisseurs();
      chargerStats();
      setTimeout(() => setSucces(''), 3000);
    } catch (e) {
      setErreur('Erreur lors de la suppression');
    }
  };

  if (chargement) return <div className="page-chargement">Chargement des fournisseurs...</div>;

  return (
    <div className="fournisseurs-page">
      <div className="page-header">
        <div>
          <h1>Fournisseurs</h1>
          <p>Gérez vos fournisseurs et partenaires commerciaux</p>
        </div>
        <button className="btn-primary" onClick={ouvrirCreation}>
          + Nouveau fournisseur
        </button>
      </div>

      {succes && <div className="alert-succes">✅ {succes}</div>}
      {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

      {/* STATS */}
      {stats && (
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-val">{stats.total}</span>
            <span className="stat-lbl">Total fournisseurs</span>
          </div>
          <div className="stat-item">
            <span className="stat-val" style={{ color: '#3B6D11' }}>{stats.actifs}</span>
            <span className="stat-lbl">Actifs</span>
          </div>
          <div className="stat-item">
            <span className="stat-val" style={{ fontSize: 16, color: '#185FA5' }}>
              {fmt(stats.totalAchats)} F
            </span>
            <span className="stat-lbl">Total achats cumulés</span>
          </div>
        </div>
      )}

      {/* RECHERCHE */}
      <div className="search-bar">
        <input
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
          placeholder="🔍 Rechercher par nom, contact ou téléphone..."
        />
      </div>

      {/* TABLEAU */}
      <div className="fournisseurs-table">
        <table>
          <thead>
            <tr>
              <th>Fournisseur</th>
              <th>Contact</th>
              <th>Téléphone</th>
              <th>Catégorie</th>
              <th>Délai livraison</th>
              <th>Total achats</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fournisseurs.map(f => (
              <tr key={f.id} className="tr-clickable" onClick={() => ouvrirDetail(f)}>
                <td>
                  <div className="f-cell">
                    <div className="f-avatar">
                      {f.nom.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="f-nom">{f.nom}</div>
                      {f.ville && <div className="f-ville">{f.ville}, {f.pays}</div>}
                    </div>
                  </div>
                </td>
                <td>{f.contact || '—'}</td>
                <td className="f-tel">{f.telephone || '—'}</td>
                <td>
                  {f.categorie && (
                    <span className="f-cat">{f.categorie}</span>
                  )}
                </td>
                <td>{f.delaiLivraison || '—'}</td>
                <td className="f-montant">{fmt(f.totalAchats)} FCFA</td>
                <td>
                  <span className={`statut-badge ${f.actif ? 'actif' : 'inactif'}`}>
                    {f.actif ? '✓ Actif' : '✗ Inactif'}
                  </span>
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="td-actions">
                    <button onClick={() => ouvrirEdition(f)}>✏️</button>
                    <button onClick={() => {
                      setFournisseurSel(f);
                      setShowDeleteModal(true);
                    }}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {fournisseurs.length === 0 && (
          <div className="table-empty">
            {recherche ? 'Aucun fournisseur trouvé' : 'Aucun fournisseur enregistré'}
          </div>
        )}
      </div>

      {/* MODAL DETAIL */}
      {showDetail && fournisseurSel && (
        <div className="modal-overlay" onClick={() => setShowDetail(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Fiche fournisseur</h2>
              <button className="modal-close" onClick={() => setShowDetail(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-header">
                <div className="detail-avatar-lg">
                  {fournisseurSel.nom.slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="detail-nom">{fournisseurSel.nom}</div>
                  {fournisseurSel.categorie && (
                    <span className="f-cat">{fournisseurSel.categorie}</span>
                  )}
                </div>
              </div>

              <div className="detail-stats">
                <div className="detail-stat">
                  <div className="detail-stat-val">{fmt(fournisseurSel.totalAchats)} F</div>
                  <div className="detail-stat-lbl">Total achats</div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat-val">{fournisseurSel.nombreCommandes}</div>
                  <div className="detail-stat-lbl">Commandes</div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat-val">
                    {fournisseurSel.actif ? '✓ Actif' : '✗ Inactif'}
                  </div>
                  <div className="detail-stat-lbl">Statut</div>
                </div>
              </div>

              <div className="detail-info-grid">
                {fournisseurSel.contact && (
                  <div className="detail-info-item">
                    <span className="detail-info-lbl">👤 Contact</span>
                    <span>{fournisseurSel.contact}</span>
                  </div>
                )}
                {fournisseurSel.telephone && (
                  <div className="detail-info-item">
                    <span className="detail-info-lbl">📞 Téléphone</span>
                    <span>{fournisseurSel.telephone}</span>
                  </div>
                )}
                {fournisseurSel.email && (
                  <div className="detail-info-item">
                    <span className="detail-info-lbl">📧 Email</span>
                    <span>{fournisseurSel.email}</span>
                  </div>
                )}
                {fournisseurSel.adresse && (
                  <div className="detail-info-item">
                    <span className="detail-info-lbl">📍 Adresse</span>
                    <span>{fournisseurSel.adresse}, {fournisseurSel.ville}</span>
                  </div>
                )}
                {fournisseurSel.ifu && (
                  <div className="detail-info-item">
                    <span className="detail-info-lbl">🏛️ IFU</span>
                    <span>{fournisseurSel.ifu}</span>
                  </div>
                )}
                {fournisseurSel.delaiLivraison && (
                  <div className="detail-info-item">
                    <span className="detail-info-lbl">🚚 Délai livraison</span>
                    <span>{fournisseurSel.delaiLivraison}</span>
                  </div>
                )}
                {fournisseurSel.conditionsPaiement && (
                  <div className="detail-info-item">
                    <span className="detail-info-lbl">💳 Conditions paiement</span>
                    <span>{fournisseurSel.conditionsPaiement}</span>
                  </div>
                )}
                <div className="detail-info-item">
                  <span className="detail-info-lbl">📅 Fournisseur depuis</span>
                  <span>{new Date(fournisseurSel.createdAt).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>

              {fournisseurSel.notes && (
                <div className="detail-notes">
                  <div className="detail-info-lbl">📝 Notes</div>
                  <p>{fournisseurSel.notes}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-danger-outline"
                onClick={() => { setShowDeleteModal(true); setShowDetail(false); }}>
                🗑️ Supprimer
              </button>
              <button className="btn-secondary" onClick={() => setShowDetail(false)}>Fermer</button>
              <button className="btn-primary" onClick={() => ouvrirEdition(fournisseurSel)}>
                ✏️ Modifier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CREATION / EDITION */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{enEdition ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

              <div className="form-section">Identification</div>
              <div className="form-group">
                <label>Nom du fournisseur <span className="req">*</span></label>
                <input value={form.nom}
                  onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                  placeholder="Ex: Tech Import Sarl" />
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Catégorie</label>
                  <select value={form.categorie}
                    onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))}>
                    <option value="">— Choisir —</option>
                    {categories.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Pays</label>
                  <select value={form.pays}
                    onChange={e => setForm(f => ({ ...f, pays: e.target.value }))}>
                    <option>Burkina Faso</option>
                    <option>Côte d'Ivoire</option>
                    <option>Ghana</option>
                    <option>Sénégal</option>
                    <option>Mali</option>
                    <option>Nigeria</option>
                    <option>Chine</option>
                    <option>Dubai / EAU</option>
                    <option>France</option>
                    <option>Autre</option>
                  </select>
                </div>
              </div>

              <div className="form-section">Contact</div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Nom du contact</label>
                  <input value={form.contact}
                    onChange={e => setForm(f => ({ ...f, contact: e.target.value }))}
                    placeholder="Ex: Moussa Kaboré" />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input value={form.telephone}
                    onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))}
                    placeholder="+226 XX XX XX XX" />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="contact@fournisseur.com" />
                </div>
                <div className="form-group">
                  <label>Ville</label>
                  <input value={form.ville}
                    onChange={e => setForm(f => ({ ...f, ville: e.target.value }))}
                    placeholder="Ex: Ouagadougou" />
                </div>
              </div>
              <div className="form-group">
                <label>Adresse</label>
                <input value={form.adresse}
                  onChange={e => setForm(f => ({ ...f, adresse: e.target.value }))}
                  placeholder="Adresse complète" />
              </div>

              <div className="form-section">Informations légales</div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>IFU</label>
                  <input value={form.ifu}
                    onChange={e => setForm(f => ({ ...f, ifu: e.target.value }))}
                    placeholder="Ex: BF-2023-XXXXXXX" />
                </div>
                <div className="form-group">
                  <label>RCCM</label>
                  <input value={form.rccm}
                    onChange={e => setForm(f => ({ ...f, rccm: e.target.value }))}
                    placeholder="Ex: BF-OUA-2023-B-XXXXX" />
                </div>
              </div>

              <div className="form-section">Conditions commerciales</div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Délai de livraison</label>
                  <input value={form.delaiLivraison}
                    onChange={e => setForm(f => ({ ...f, delaiLivraison: e.target.value }))}
                    placeholder="Ex: 3-5 jours ouvrables" />
                </div>
                <div className="form-group">
                  <label>Conditions de paiement</label>
                  <input value={form.conditionsPaiement}
                    onChange={e => setForm(f => ({ ...f, conditionsPaiement: e.target.value }))}
                    placeholder="Ex: 30 jours, à la commande..." />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  placeholder="Informations complémentaires..."
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 7, fontFamily: 'inherit', fontSize: 13 }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={sauvegarder}>
                {enEdition ? 'Enregistrer' : 'Créer le fournisseur'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SUPPRESSION */}
      {showDeleteModal && fournisseurSel && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header danger">
              <h2>⚠️ Supprimer le fournisseur</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Voulez-vous supprimer <strong>{fournisseurSel.nom}</strong> ?
              Cette action est irréversible.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>Annuler</button>
              <button className="btn-danger" onClick={supprimer}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}